import { useState, useEffect, useCallback } from 'react'
import { aiApi, AnalysisResponse } from '../api/ai'

interface AnalysisJobsPanelProps {
  onLoadJob: (job: AnalysisResponse) => void
  activeAnalysisId?: string | null
  activeSessionId?: string
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-700',
  running: 'bg-blue-100 text-blue-700',
  complete: 'bg-emerald-100 text-emerald-700',
  error: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function AnalysisJobsPanel({ onLoadJob, activeAnalysisId, activeSessionId }: AnalysisJobsPanelProps) {
  const [jobs, setJobs] = useState<AnalysisResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const fetchJobs = useCallback(async () => {
    if (!activeSessionId) {
      setJobs([])
      return
    }
    try {
      const res = await aiApi.listAnalyses(activeSessionId)
      setJobs(res.data)
      setError('')
    } catch (e: any) {
      setError('Failed to load jobs')
    }
  }, [activeSessionId])

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  // Auto-poll if any job is pending or running
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'pending' || j.status === 'running')
    if (!hasActive) return
    const interval = setInterval(fetchJobs, 3000)
    return () => clearInterval(interval)
  }, [jobs, fetchJobs])

  const handleCancel = async (id: string) => {
    setLoading(true)
    try {
      await aiApi.cancelAnalysis(id)
      fetchJobs()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Cancel failed')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this analysis? This cannot be undone.')) return
    setLoading(true)
    try {
      await aiApi.deleteAnalysis(id)
      fetchJobs()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Delete failed')
    } finally {
      setLoading(false)
    }
  }

  const [loadingJobId, setLoadingJobId] = useState<string | null>(null)

  const handleLoad = async (jobId: string) => {
    setLoadingJobId(jobId)
    try {
      const res = await aiApi.getAnalysis(jobId)
      onLoadJob(res.data)
      localStorage.setItem('pmc_ev_last_analysis_id', jobId)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load job')
    } finally {
      setLoadingJobId(null)
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Analysis Jobs</h3>
        <button
          onClick={fetchJobs}
          disabled={loading}
          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && <div className="mb-2 text-xs text-red-600">{error}</div>}

      {jobs.length === 0 ? (
        <div className="text-xs text-gray-500 py-2">
          {activeSessionId ? 'No analyses in this session yet.' : 'Select a session to see its analyses.'}
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {jobs.map(job => {
            const isActive = job.analysis_id === activeAnalysisId
            return (
              <div
                key={job.analysis_id}
                className={`rounded-lg p-2.5 text-sm border ${
                  isActive ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_STYLES[job.status] || 'bg-gray-100 text-gray-600'}`}>
                    {job.status}
                  </span>
                  <span className="text-xs text-gray-400 ml-auto">{timeAgo(job.created_at)}</span>
                </div>
                <div className="font-medium text-gray-800 truncate" title={job.name}>
                  {job.name}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {job.params?.output_format === 'table' ? 'Table' : 'Text'} · {job.params?.data_count || '?'} rows
                </div>
                <div className="flex gap-2 mt-2">
                  {job.status === 'complete' && (
                    <button
                      onClick={() => handleLoad(job.analysis_id)}
                      disabled={loadingJobId === job.analysis_id}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loadingJobId === job.analysis_id ? 'Loading...' : 'Load'}
                    </button>
                  )}
                  {(job.status === 'pending' || job.status === 'running') && (
                    <button
                      onClick={() => handleCancel(job.analysis_id)}
                      disabled={loading}
                      className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(job.analysis_id)}
                    disabled={loading}
                    className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
